import { z } from 'zod';
import { ParticipantSchema } from '../projects/ProjectFormData';
import { BenefitType } from '../benefits/benefitTypes';
import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { MarginRateSchema } from '../common/margin-rate.schema';

/** Number inputs often submit '' when cleared; treat as undefined instead of NaN. */
const optionalNonNegativeNumber = z.preprocess(
  (val) => (val === '' || val === null || val === undefined ? undefined : val),
  z.coerce
    .number({ message: 'La valeur doit être un nombre' })
    .min(0, 'La valeur doit être supérieure ou égale à 0')
    .optional(),
);

const requiredNonNegativeNumber = z.preprocess(
  (val) => (val === '' || val === null || val === undefined ? 0 : val),
  z.coerce
    .number({ message: 'Le tarif doit être un nombre' })
    .min(0, 'Le tarif doit être supérieur ou égal à 0'),
);

const optionalBooleanRadio = z.preprocess(
  (val) => (val === null || val === '' ? undefined : val),
  z.enum(['true', 'false']).optional(),
);

const EstimateMainContactSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z
    .union([
      z.literal(''),
      z.string().email({ message: "L'email doit être valide" }),
    ])
    .optional()
    .transform((value) => (value === '' ? undefined : value)),
  phoneNumber: z
    .string()
    .regex(
      /^\+?[0-9]+$/,
      'Le numéro de téléphone ne doit contenir que des chiffres ou un + au début',
    )
    .min(5, 'Le numéro de téléphone doit contenir au moins 5 caractères')
    .or(z.literal(''))
    .optional()
    .transform((value) => (value === '' ? undefined : value)),
  address: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
});

const carCategoryElementSchema = z.object({
  elementId: z.string(),
  deltaPrice: z.coerce.number(),
  isRequired: z.boolean().optional(),
});

const EstimateOptionSchema = z.object({
  deltaPrice: z.coerce.number(),
  type: z.nativeEnum(BenefitType),
  benefitId: z.string(),
  carCategoryId: z.string().optional(),
  hotelCategoryId: z.string().optional(),
  roomCombination: z.array(z.array(z.number()).length(3)).optional(),
  hotelPensionType: z.union([z.string(), z.null()]).optional(),
  carCategoryOptions: z
    .object({
      insurances: z.array(carCategoryElementSchema).optional(),
      options: z.array(carCategoryElementSchema).optional(),
      taxes: z.array(carCategoryElementSchema).optional(),
      oneWayFees: z.array(carCategoryElementSchema).optional(),
    })
    .optional(),
});

const selectedEstimateOptionSchema = z.record(z.string(), EstimateOptionSchema);

/**
 * Validation for estimate save / reservation forms.
 * The circuit snapshot is not re-validated here (already loaded from the API);
 * participant names and birth dates are only required when sending a reservation
 * from the distributor / agency workspace (not on the client shared link).
 */
export const EstimateSchema = z.object({
  circuit: z.custom<CircuitFormDataWithTranslations>(() => true),
  marginRate: MarginRateSchema.optional(),
  adults: z
    .array(ParticipantSchema)
    .min(1, { message: 'Au moins un adulte est requis' }),
  children: z.array(ParticipantSchema),
  customParagraphTitle: z.string().optional(),
  customParagraphDescription: z.string().optional(),
  internationalFlightsPrice: requiredNonNegativeNumber,
  internationalFlightsNonMarginedTaxes: optionalNonNegativeNumber,
  internationalFlightsMarginRate: MarginRateSchema.optional(),
  marginRateExistsForInternationalFlights: optionalBooleanRadio,
  agencyMarginRate: MarginRateSchema.optional(),
  agencyInternationalFlightsPrice: optionalNonNegativeNumber,
  agencyInternationalFlightsNonMarginedTaxes: optionalNonNegativeNumber,
  agencyInternationalFlightsMarginRate: MarginRateSchema.optional(),
  agencyMarginRateExistsForInternationalFlights: optionalBooleanRadio,
  agencyAdditionalMarginType: z.enum(['FIXED', 'PERCENT']).optional(),
  agencyAdditionalMarginValue: optionalNonNegativeNumber,
  mainContact: EstimateMainContactSchema,
  selectedEstimateOptions: selectedEstimateOptionSchema,
});

export type EstimateFormData = z.infer<typeof EstimateSchema>;

export const validateEstimateReservationParticipants = (
  data: Pick<EstimateFormData, 'adults' | 'children'>,
): string | undefined => {
  const hasAdultNames = data.adults.every(
    ({ firstName, lastName }) =>
      firstName != undefined &&
      firstName.trim() !== '' &&
      lastName != undefined &&
      lastName.trim() !== '',
  );
  if (!hasAdultNames) {
    return 'Les noms des adultes sont obligatoires';
  }

  const hasChildDetails = data.children.every(
    ({ firstName, lastName, birthDate }) =>
      birthDate != undefined &&
      firstName != undefined &&
      firstName.trim() !== '' &&
      lastName != undefined &&
      lastName.trim() !== '',
  );
  if (!hasChildDetails) {
    return 'Les noms et dates de naissance des enfants sont obligatoires';
  }

  return undefined;
};
